import { useState, useEffect, useRef, useMemo } from 'preact/hooks'
import { X, Loader2, Send, Braces } from 'lucide-preact'
import { useWebsterBuilder, type WebsterAssetType } from '@/hooks/useWebsterBuilder'
import { useSendTestEmail } from '@/hooks/useSendTestEmail'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { ASSET_TYPE_META, DEFAULT_ASSET_META } from '@/components/webs/constants/assetMeta'

import { RevertDraftButton } from '@/components/assets/RevertDraftButton'
import { LandingPageBuilder } from '@/components/webs/overlays/LandingPageBuilder'
import { EmailBuilder } from '@/components/webs/overlays/EmailBuilder'
import { SaveAsVersionButton, computeSaveAsVersionState } from '@/components/webs/overlays/WebsterBuilderShared'
import { VariableCatalogPanel } from '@/components/webs/overlays/VariableCatalogPanel'
import { GenerationEffortContext, type GenerationEffortControl } from '@/components/webster/generationEffort'
import { COPY_FOOTER_GROUP, TEMPLATE_SLOT_GROUP } from '@/components/assets/emailMergeTags'
import type { VariableSurface } from '@/types/lookups'

interface WebsterBuilderOverlayProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    assetId: string
    assetType: WebsterAssetType
    onClose: () => void
    onAssetUpdated?: () => void
}

export function WebsterBuilderOverlay({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId,
    assetId,
    assetType,
    onClose,
    onAssetUpdated,
}: WebsterBuilderOverlayProps) {
    // The Save-as-Version button pulls a flush callback off this ref. EmailBuilder fills
    // it in with logic that forces Unlayer to export its current design + HTML before
    // promote runs — covers the "fresh Webster-generated asset has DraftDesign but null
    // DraftHtml until the editor's first auto-update fires" case that otherwise fails
    // the server's "Body Required" check.
    const emailFlushRef = useRef<(() => Promise<boolean | void>) | null>(null)

    // EmailBuilder registers its refetch+reseed routine here so the top-bar "Revert to
    // last version" button can reload the editor off the restored draft after a revert.
    const emailReseedRef = useRef<(() => Promise<void>) | null>(null)

    // EmailBuilder fills this with a "compile the current editor HTML" callback so the
    // top-bar Send Test button can mail the live, unsaved design as an HtmlOverride (ORB-441).
    const emailGetHtmlRef = useRef<(() => Promise<string>) | null>(null)

    const {
        assetDetail,
        versions,
        loading: assetLoading,
        promoting,
        promoteDraft,
        hasLandingPage,
        refreshAsset,
        generationEffort,
        savingGenerationEffort,
        updateGenerationEffort,
    } = useWebsterBuilder({
        clientCompanyId,
        workspaceId,
        campaignId,
        assetId,
        assetType,
        onAssetUpdated,
    })

    // Send Test (ORB-441) — reuses the Email Copy test-send path. Gated on the same editor
    // capability the endpoint enforces (canEdit ≡ IsWorkspaceEditor) so the button never
    // shows to someone the server would 403. Email Copy only — templates aren't sent.
    const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
    const { sendTest, sendingTest } = useSendTestEmail()

    const handleSendTest = async () => {
        // Live editor HTML so unsaved edits are tested as-is; no sending domain → the
        // server uses the platform notification sender (copy-editor test).
        const htmlOverride = await emailGetHtmlRef.current?.()
        await sendTest({
            clientCompanyId,
            workspaceId,
            campaignId,
            assetId,
            body: { HtmlOverride: htmlOverride || undefined },
        })
    }

    const generationEffortControl = useMemo<GenerationEffortControl | null>(
        () => generationEffort
            ? { value: generationEffort, onChange: updateGenerationEffort, saving: savingGenerationEffort }
            : null,
        [generationEffort, savingGenerationEffort, updateGenerationEffort],
    )

    const meta = ASSET_TYPE_META[assetType] || DEFAULT_ASSET_META

    // ORB-466: token reference panel. Which surfaces the panel offers is decided here because
    // this is the one place that knows the asset type. The mapping is deliberately narrow —
    // showing a user a token that does nothing where they are is the bug this closes:
    //   Landing Page   → landing page only; it has no recipient, so {{first_name}} must not appear.
    //   Email Template → body only; the template editor has no subject field.
    //   Email Copy     → body and subject, the two things it actually edits.
    const [variablesOpen, setVariablesOpen] = useState(false)
    const variableSurfaces = useMemo<readonly VariableSurface[]>(() => {
        if (assetType === 'Landing Page') return ['LandingPage']
        if (assetType === 'Email Copy') return ['EmailBody', 'EmailSubject']
        return ['EmailBody']
    }, [assetType])

    // LP readiness — Save as Version disabled until thank you page is generated
    const [lpReadyForVersion, setLpReadyForVersion] = useState(false)
    // For Landing Pages the authoritative DraftUpdatedAt lives on the
    // LandingPage entity, not the WebAsset (matches the server's PromoteAsset
    // check in PromoteAssetDraft.cs). Held here, pushed up by LandingPageBuilder.
    const [lpDraftUpdatedAt, setLpDraftUpdatedAt] = useState<string | null>(null)
    const draftUpdatedAt = assetType === 'Landing Page'
        ? lpDraftUpdatedAt
        : assetDetail?.DraftUpdatedAt ?? null

    // Generation-in-flight gate. Bubbled up from the active builder (LP or Email) so
    // Save-as-Version can be disabled while Webster is generating — initial generation
    // OR a chat-driven rebuild. Promoting a draft that's mid-write would freeze a partial
    // state as the canonical version. Paired with the server-persisted DraftGenerationStatus
    // below, which is authoritative on reopen / before the builder first reports up.
    const [generating, setGenerating] = useState(false)

    // Why the email editor can't be saved from yet: still loading, or its design is empty (ORB-960).
    const [editorBlockedReason, setEditorBlockedReason] = useState<string | null>(null)

    const saveState = computeSaveAsVersionState({
        assetType,
        lpReadyForVersion,
        emailCopyTemplateAssetId: assetDetail?.EmailTemplateAssetId ?? null,
        versions,
        currentVersionUploadedAt: assetDetail?.CurrentVersionUploadedAt ?? null,
        draftUpdatedAt,
        generating,
        draftGenerationStatus: assetDetail?.DraftGenerationStatus ?? null,
        editorBlockedReason: assetType === 'Landing Page' ? null : editorBlockedReason,
    })

    // Escape key closes overlay
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [onClose])

    // Lock body scroll + hide AI chat widget
    useBodyScrollLock()

    if (assetLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-slate-900">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">Loading builder...</span>
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900">
            {/* Top bar */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        title="Close builder"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                    <div className={`flex items-center justify-center w-7 h-7 rounded-md ${meta.badgeBg}`}>
                        <meta.Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {assetDetail?.Name || assetType}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                        Webster Builder
                    </span>
                </div>

                {/* Save as Version button + draft-ahead-of-version indicator */}
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setVariablesOpen(true)}
                        title="See the variables you can use here and what they resolve to"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                    >
                        <Braces className="h-3.5 w-3.5" aria-hidden="true" />
                        Variables
                    </button>
                    {assetType === 'Email Copy' && canEdit && (
                        <button
                            type="button"
                            onClick={handleSendTest}
                            disabled={sendingTest || generating}
                            title="Send a test of this email to your own address"
                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Send className="h-3.5 w-3.5" aria-hidden="true" />
                            {sendingTest ? 'Sending…' : 'Send test to me'}
                        </button>
                    )}
                    {saveState.draftAheadOfVersion && saveState.latestVersion && (
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                            Draft updated since v{saveState.latestVersion.VersionNumber}
                        </span>
                    )}
                    {/* Revert applies to both email types — their versions store the editable design
                        JSON, so the editor reseeds off the restored draft — and only when the draft has
                        actually drifted from the latest version; otherwise there's nothing to revert and
                        the action is a confusing no-op (ORB-301). Excludes Landing Page. */}
                    {assetType !== 'Landing Page' && saveState.latestVersion && saveState.draftAheadOfVersion && (
                        <RevertDraftButton
                            clientCompanyId={clientCompanyId}
                            workspaceId={workspaceId}
                            campaignId={campaignId}
                            assetId={assetId}
                            versionNumber={saveState.latestVersion.VersionNumber}
                            disabled={promoting || generating}
                            onReverted={async () => { await emailReseedRef.current?.() }}
                        />
                    )}
                    <SaveAsVersionButton
                        assetType={assetType}
                        promoting={promoting}
                        onPromoteDraft={promoteDraft}
                        hasLandingPage={hasLandingPage}
                        disabled={saveState.saveDisabled}
                        disabledReason={saveState.saveDisabledReason}
                        emailFlushRef={emailFlushRef}
                    />
                </div>
            </div>

            {/* Builder area — type-specific layout */}
            <div className="flex-1 overflow-hidden">
                <GenerationEffortContext.Provider value={generationEffortControl}>
                    {assetType === 'Landing Page' ? (
                        <LandingPageBuilder
                            clientCompanyId={clientCompanyId}
                            workspaceId={workspaceId}
                            campaignId={campaignId}
                            webId={webId}
                            assetId={assetId}
                            onPromoteDraft={promoteDraft}
                            promoting={promoting}
                            onReadyForVersionChange={setLpReadyForVersion}
                            onLandingPageEstablished={refreshAsset}
                            onLandingPageRebuilt={refreshAsset}
                            onDraftUpdatedAtChange={setLpDraftUpdatedAt}
                            onGeneratingChange={setGenerating}
                        />
                    ) : (
                        <EmailBuilder
                            clientCompanyId={clientCompanyId}
                            workspaceId={workspaceId}
                            campaignId={campaignId}
                            webId={webId}
                            assetId={assetId}
                            assetType={assetType as 'Email Template' | 'Email Copy'}
                            assetDetail={assetDetail}
                            refreshAsset={refreshAsset}
                            flushRef={emailFlushRef}
                            getHtmlRef={emailGetHtmlRef}
                            reseedRef={emailReseedRef}
                            onGeneratingChange={setGenerating}
                            versions={versions}
                            onEditorBlockedChange={setEditorBlockedReason}
                        />
                    )}
                </GenerationEffortContext.Provider>
            </div>

            <VariableCatalogPanel
                open={variablesOpen}
                onClose={() => setVariablesOpen(false)}
                workspaceId={workspaceId}
                webId={webId}
                surfaces={variableSurfaces}
                extraGroup={assetType === 'Email Template' ? TEMPLATE_SLOT_GROUP : assetType === 'Email Copy' ? COPY_FOOTER_GROUP : null}
            />
        </div>
    )
}
